import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import moment from 'moment'
import { FaRegCommentDots } from 'react-icons/fa'
import { API_BASE } from '../lib/api'
import MarkdownMessage from '../components/MarkdownMessage'

type ChatMessage = {
    id: string | number
    sender: 'user' | 'assistant'
    user_name?: string | null
    text: string
    created_at: string
}

type SharedData = {
    thread: {
        id: number
        title: string
        created_at: string
    }
    messages: ChatMessage[]
}

const formatTimestamp = (value: string) => {
    const m = moment(value)
    return m.isValid() ? m.format('lll') : ''
}

export default function SharedChat() {
    const { token } = useParams<{ token: string }>()
    const [data, setData] = useState<SharedData | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    useEffect(() => {
        const fetchSharedChat = async () => {
            try {
                const res = await fetch(`${API_BASE}/api/chat/shared/${token}`)
                if (!res.ok) {
                    if (res.status === 404) {
                        setError('Shared chat not found or link is invalid.')
                    } else {
                        setError('Failed to load shared chat.')
                    }
                    return
                }
                const json = await res.json()
                setData(json)
            } catch (err) {
                console.error(err)
                setError('An error occurred while fetching the shared chat.')
            } finally {
                setLoading(false)
            }
        }
        
        if (token) {
            fetchSharedChat()
        }
    }, [token])

    if (loading) {
        return (
            <div className="h-screen w-full flex items-center justify-center bg-slate-50 text-slate-500">
                Loading shared chat...
            </div>
        )
    }

    if (error || !data) {
        return (
            <div className="h-screen w-full flex items-center justify-center bg-slate-50">
                <div className="bg-white p-8 rounded-lg shadow-md border border-rose-200 text-center max-w-md">
                    <h2 className="text-xl font-semibold text-rose-700 mb-2">Unavailable</h2>
                    <p className="text-slate-600">{error}</p>
                </div>
            </div>
        )
    }

    return (
        <div className="h-screen w-full bg-slate-100 flex flex-col p-4 sm:p-8 items-center">
            <main className="w-full max-w-3xl flex flex-col min-h-0 bg-white rounded-lg shadow-md border border-slate-200 overflow-hidden">
                <header className="border-b border-slate-200 p-4 shrink-0 bg-slate-50 flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-semibold text-slate-900">Shared Chat</h1>
                        <p className="mt-0.5 text-sm text-slate-500 truncate">
                            {data.thread.title}
                        </p>
                    </div>
                    <div className="text-xs text-slate-400 bg-white px-3 py-1 rounded-full border border-slate-200 shadow-sm">
                        Read Only
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
                    {data.messages.length === 0 ? (
                        <div className="flex justify-center mt-10">
                            <div className="text-slate-500 text-sm">No messages in this chat.</div>
                        </div>
                    ) : (
                        data.messages.map((message) => {
                            const isUser = message.sender === 'user'
                            const senderName = isUser
                                ? (message.user_name ?? 'User')
                                : 'Filtron AI'
                            return (
                                <div key={message.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                                    <div style={{ maxWidth: isUser ? '75%' : '90%' }} className={`rounded-lg px-4 py-3 shadow-sm ${isUser ? 'bg-slate-700 text-white' : 'border border-slate-200 bg-white text-slate-900'}`}>
                                        <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
                                            <FaRegCommentDots className={isUser ? 'text-slate-200' : 'text-blue-600'} />
                                            {senderName}
                                        </div>
                                        {isUser ? (
                                            <p className="whitespace-pre-wrap">{message.text}</p>
                                        ) : (
                                            <MarkdownMessage text={message.text} className="text-slate-900" />
                                        )}
                                        <div className={`mt-2 text-xs ${isUser ? 'text-slate-300' : 'text-slate-500'}`}>
                                            {formatTimestamp(message.created_at)}
                                        </div>
                                    </div>
                                </div>
                            )
                        })
                    )}
                </div>
            </main>
        </div>
    )
}
